import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { inputCls, canDo } from "./shared";

const FIT_LABELS = { slim: "Slim", regular: "Regular", relaxed: "Relaxed", oversized: "Oversized" };

export function SizeChartsTab({ user }) {
  const [charts, setCharts] = useState([]);
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState(null); // chart being edited
  const [draft, setDraft] = useState(null);
  const canEdit = canDo(user, "products", "edit");

  const load = () => {
    api.get("/admin/size-charts").then((r) => {
      setCharts(r.data);
      if (!active && r.data.length) openChart(r.data[0]);
      else if (active) openChart(r.data.find((c) => c.key === active) || r.data[0]);
    }).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/fit/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  const openChart = (c) => {
    setActive(c.key);
    setDraft({ sizes: c.sizes.map((s) => ({ ...s })), ease: { ...c.ease }, model: { ...(c.model || {}) } });
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const measureKeys = charts.find((c) => c.key === active)?.kind === "bottom" ? ["waist", "hip", "inseam"] : ["chest", "shoulder", "length"];

  const setSizeVal = (i, k, v) => {
    const sizes = draft.sizes.map((s, x) => (x === i ? { ...s, [k]: k === "size" ? v : parseFloat(v) || 0 } : s));
    setDraft({ ...draft, sizes });
  };

  const save = async () => {
    try {
      await api.put(`/admin/size-charts/${active}`, draft);
      toast.success("Size chart saved — recommendations update immediately");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!draft) return <p className="text-vv-muted text-sm py-10 animate-pulse">Loading size charts…</p>;
  const chart = charts.find((c) => c.key === active);

  return (
    <div data-testid="size-charts-tab" className="max-w-4xl">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="fit-stats">
          {[["Opens", stats.openings], ["Recommendations", stats.completions],
            ["Add to Bag after", stats.add_to_carts], ["Conversion", `${stats.conversion}%`]].map(([l, v]) => (
            <div key={l} className="border border-vv-black/10 p-4">
              <p className="text-[9px] uppercase tracking-[0.25em] text-vv-stone">{l}</p>
              <p className="font-display text-2xl mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}
      {stats && (stats.feedback_helpful + stats.feedback_wrong) > 0 && (
        <p className="text-[11px] text-vv-muted mb-8">
          Customer feedback: {stats.feedback_helpful} helpful · {stats.feedback_wrong} not right ·
          Recommended sizes: {Object.entries(stats.size_distribution).map(([s, n]) => `${s}×${n}`).join(", ") || "—"}
        </p>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {charts.map((c) => (
          <button key={c.key} data-testid={`chart-tab-${c.key}`} onClick={() => openChart(c)}
            className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${active === c.key ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <fieldset disabled={!canEdit}>
        <div className="border border-vv-black/10 p-5 mb-6">
          <p className="vv-overline text-vv-muted mb-4">Garment measurements (cm)</p>
          <table className="w-full text-xs" data-testid="chart-editor">
            <thead>
              <tr className="text-left text-[9px] uppercase tracking-[0.2em] text-vv-stone">
                <th className="py-2 pr-2">Size</th>
                {measureKeys.map((k) => <th key={k} className="pr-2 capitalize">{k}</th>)}
              </tr>
            </thead>
            <tbody>
              {draft.sizes.map((s, i) => (
                <tr key={i}>
                  <td className="py-1.5 pr-2"><input value={s.size} onChange={(e) => setSizeVal(i, "size", e.target.value)} className={`${inputCls} !py-1.5 !px-2 w-16`} data-testid={`chart-size-${i}`} /></td>
                  {measureKeys.map((k) => (
                    <td key={k} className="pr-2"><input type="number" value={s[k] ?? ""} onChange={(e) => setSizeVal(i, k, e.target.value)} className={`${inputCls} !py-1.5 !px-2 w-20`} data-testid={`chart-${k}-${i}`} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="border border-vv-black/10 p-5">
            <p className="vv-overline text-vv-muted mb-4">Ease allowance by fit (cm)</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.keys(FIT_LABELS).map((f) => (
                <div key={f}>
                  <label className="text-[9px] uppercase tracking-[0.2em] text-vv-stone">{FIT_LABELS[f]}</label>
                  <input type="number" value={draft.ease[f] ?? 0} onChange={(e) => setDraft({ ...draft, ease: { ...draft.ease, [f]: parseFloat(e.target.value) || 0 } })}
                    className={`${inputCls} mt-1`} data-testid={`chart-ease-${f}`} />
                </div>
              ))}
            </div>
          </div>
          <div className="border border-vv-black/10 p-5">
            <p className="vv-overline text-vv-muted mb-4">Model measurements</p>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-[9px] uppercase tracking-[0.2em] text-vv-stone">Height cm</label>
                <input type="number" value={draft.model.height ?? ""} onChange={(e) => setDraft({ ...draft, model: { ...draft.model, height: parseInt(e.target.value) || null } })} className={`${inputCls} mt-1`} data-testid="chart-model-height" /></div>
              <div><label className="text-[9px] uppercase tracking-[0.2em] text-vv-stone">Weight kg</label>
                <input type="number" value={draft.model.weight ?? ""} onChange={(e) => setDraft({ ...draft, model: { ...draft.model, weight: parseInt(e.target.value) || null } })} className={`${inputCls} mt-1`} data-testid="chart-model-weight" /></div>
              <div><label className="text-[9px] uppercase tracking-[0.2em] text-vv-stone">Wears</label>
                <input value={draft.model.wears ?? ""} onChange={(e) => setDraft({ ...draft, model: { ...draft.model, wears: e.target.value } })} className={`${inputCls} mt-1`} data-testid="chart-model-wears" /></div>
            </div>
          </div>
        </div>

        {canEdit && <button data-testid="chart-save" onClick={save} className="vv-btn-dark">Save {chart?.label} Chart</button>}
      </fieldset>
      {!canEdit && <p className="text-[11px] text-vv-muted mt-4">View only — your role cannot edit size charts.</p>}
    </div>
  );
}
